import { useState } from 'react'
import { ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

// Same env var App.tsx uses for the Apollo client; the REST API lives on the
// same origin as /graphql.
const API_BASE = (
  process.env.REACT_APP_API_URL ?? 'http://localhost:4000/graphql'
).replace(/\/graphql\/?$/, '')

interface InvoicePdfLinkProps {
  invoicePdfPath: string // backend-relative path from Order.invoice_pdf_url
  invoiceCode: string | null
}

export function InvoicePdfLink({
  invoicePdfPath,
  invoiceCode,
}: InvoicePdfLinkProps) {
  const [loading, setLoading] = useState(false)

  const openInvoice = async () => {
    if (loading) return
    // Open the tab synchronously so popup blockers don't eat the async open.
    const pdfTab = window.open('', '_blank')
    setLoading(true)
    try {
      const token = localStorage.getItem('furnisystems-token')
      const response = await fetch(`${API_BASE}${invoicePdfPath}`, {
        headers: { authorization: token ? `Bearer ${token}` : '' },
      })
      if (!response.ok) {
        pdfTab?.close()
        toast.error(
          response.status === 404
            ? 'Invoice PDF not available'
            : `Failed to load invoice PDF (${response.status})`
        )
        return
      }
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      if (pdfTab) {
        pdfTab.location.href = blobUrl
      } else {
        window.open(blobUrl, '_blank')
      }
    } catch {
      pdfTab?.close()
      toast.error('Failed to load invoice PDF')
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={openInvoice}
      disabled={loading}
      className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
    >
      {invoiceCode ? `${invoiceCode}.pdf` : 'Invoice'}
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      ) : (
        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
      )}
    </button>
  )
}
